import type { ApiService } from './apiService';
import type { BusinessDomain, BusinessRule, PimField } from '../types/businessRules';

export interface ConditionField { key: string; name?: string; datatype?: string; cvlId?: string; isMultiValue: boolean; isLocalizedCvl?: boolean; }
export interface ConditionCvlOption { key: string; label: string; }

export interface DurableWorkflow {
    instanceId: string;
    name?: string;
    runtimeStatus?: string;
    createdAt?: string;
    lastUpdatedAt?: string;
    isRunning: boolean;
    totalTasks: number;
    completedTasks: number;
    activeTasks: number;
}

export type ValidationRunStatus = 'Queued' | 'Running' | 'CancellationRequested' | 'Cancelled' | 'Completed' | 'CompletedWithErrors' | 'Failed';

export interface ValidationWorkItem {
    workItemId: string;
    batchId: string;
    ordinal: number;
    articleCount: number;
    status: ValidationRunStatus;
    attemptCount: number;
    processedCount: number;
    skippedCount: number;
    failedCount: number;
    findingCount: number;
    startedAt?: string;
    completedAt?: string;
    failureMessage?: string;
}

export interface ValidationBatch {
    batchId: string;
    runId: string;
    instanceId: string;
    owner: string;
    status: ValidationRunStatus;
    itemcount: number;
    processedCount: number;
    skippedCount: number;
    failedCount: number;
    findingCount: number;
    workItems: ValidationWorkItem[];
}

export interface ValidationRun {
    runId: string;
    displayName: string;
    requestedBy: string;
    status: ValidationRunStatus;
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
    ownerCount: number;
    totalItems: number;
    processedCount: number;
    skippedCount: number;
    failedCount: number;
    findingCount: number;
    failureMessage?: string;
    batches?: ValidationBatch[];
}

export interface ValidationRunPage { items: ValidationRun[]; total: number; page: number; pageSize: number; }
export interface ValidationKickoffResult { runId: string; displayName: string; status: ValidationRunStatus; ownerCount: number; createdAt: string; }

// Accept responses from older Functions hosts during rolling deployments.
function normalizeValidationResponse<T>(payload: unknown): T {
    const normalize = (value: unknown): unknown => {
        if (Array.isArray(value)) return value.map(normalize);
        if (value && typeof value === 'object') {
            return Object.fromEntries(Object.entries(value).map(([key, entry]) =>
                [key.charAt(0).toLowerCase() + key.slice(1), normalize(entry)]));
        }
        return value;
    };
    return normalize(payload) as T;
}

function validationId(id: string): string {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id ?? '')) {
        throw new Error('A valid validation identifier is required. Refresh the validation runs.');
    }
    return id;
}

function collectionFrom<T>(payload: unknown, endpoint: string): T[] {
    if (Array.isArray(payload)) return payload as T[];

    if (payload && typeof payload === 'object') {
        const envelope = payload as Record<string, unknown>;
        for (const property of ['$values', 'value', 'items']) {
            if (Array.isArray(envelope[property])) return envelope[property] as T[];
        }
    }

    throw new Error(`Invalid collection response from ${endpoint}.`);
}

export class BusinessRuleApiService {
    private readonly api: ApiService;

    constructor(api: ApiService) {
        this.api = api;
    }

    getRules = async (): Promise<BusinessRule[]> =>
        collectionFrom<BusinessRule>(await this.api.get<unknown>('/business-rules'), '/business-rules');

    getDomains = async (): Promise<BusinessDomain[]> =>
        collectionFrom<BusinessDomain>(await this.api.get<unknown>('/business-rules/domains'), '/business-rules/domains');

    createDomain = (name: string): Promise<BusinessDomain> => this.api.post('/business-rules/domains', { name });
    updateDomain = (id: number, name: string): Promise<BusinessDomain> => this.api.put(`/business-rules/domains/${id}`, { name });

    getFields = (entity?: string, datatype?: 'numeric' | 'localized-text'): Promise<PimField[]> => {
        const parameters = new URLSearchParams();
        if (entity) parameters.set('entity', entity);
        if (datatype) parameters.set('datatype', datatype);
        const query = parameters.size > 0 ? `?${parameters.toString()}` : '';
        return this.api.get<unknown>(`/business-rules/fields${query}`)
            .then(payload => collectionFrom<PimField>(payload, '/business-rules/fields'));
    };

    getEntities = async (): Promise<string[]> =>
        collectionFrom<string>(await this.api.get<unknown>('/business-rules/entities'), '/business-rules/entities');

    getConditionFields = async (entity: string): Promise<ConditionField[]> =>
        collectionFrom<ConditionField>(await this.api.get(`/business-rules/condition-fields?entity=${encodeURIComponent(entity)}`), '/business-rules/condition-fields');

    getConditionCvlOptions = async (field: string): Promise<ConditionCvlOption[]> =>
        collectionFrom<ConditionCvlOption>(await this.api.get(`/business-rules/condition-fields/${encodeURIComponent(field)}/cvl-options`), '/business-rules/condition-fields/cvl-options');

    getLocales = async (): Promise<string[]> =>
        collectionFrom<string>(await this.api.get('/business-rules/locales'), '/business-rules/locales');

    createRule = (rule: BusinessRule): Promise<BusinessRule> => this.api.post('/business-rules', rule);
    regenerateRuleCodes = (): Promise<{ updated: number }> => this.api.post('/business-rules/regenerate-codes', {});

    updateRule = (rule: BusinessRule): Promise<BusinessRule> => this.api.put(`/business-rules/${rule.id}`, rule);

    deleteRule = (id: number): Promise<void> => this.api.delete(`/business-rules/${id}`);

    kickOffValidation = async (): Promise<ValidationKickoffResult> =>
        normalizeValidationResponse(await this.api.post('/business-rules/validation/kickoff', {}));

    getValidationRuns = (page = 1, pageSize = 25, status?: ValidationRunStatus): Promise<ValidationRunPage> => {
        const parameters = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (status) parameters.set('status', status);
        return this.api.get<unknown>(`/validation-runs?${parameters.toString()}`)
            .then(payload => normalizeValidationResponse<ValidationRunPage>(payload));
    };

    getValidationRun = async (runId: string): Promise<ValidationRun> =>
        normalizeValidationResponse(await this.api.get(`/validation-runs/${validationId(runId)}`));
    cancelValidationRun = async (runId: string): Promise<void> => this.api.post(`/validation-runs/${validationId(runId)}/cancel`, {});
    cancelValidationBatch = async (runId: string, batchId: string): Promise<void> => this.api.post(`/validation-runs/${validationId(runId)}/batches/${validationId(batchId)}/cancel`, {});
    cancelValidationWorkItem = async (runId: string, workItemId: string): Promise<void> => this.api.post(`/validation-runs/${validationId(runId)}/work-items/${validationId(workItemId)}/cancel`, {});
    deleteValidationRun = async (runId: string): Promise<void> => this.api.delete(`/validation-runs/${validationId(runId)}`);

    getWorkflows = async (status = 'running,pending,suspended'): Promise<DurableWorkflow[]> => {
        const endpoint = `/business-rules/workflows?status=${encodeURIComponent(status)}`;
        return collectionFrom<DurableWorkflow>(await this.api.get<unknown>(endpoint), endpoint);
    };

    manageWorkflow = (operation: 'pause' | 'resume' | 'terminate', instanceId: string): Promise<void> => {
        const query = instanceId ? `?instanceId=${encodeURIComponent(instanceId)}` : '';
        return this.api.post(`/business-rules/workflows/${operation}${query}`, {});
    };

    terminateWorkflows = (instanceIds: string[]): Promise<{ requested: number; terminated: number }> =>
        this.api.post('/business-rules/workflows/terminate-selected', { instanceIds });
}
